import { useEffect, useMemo, useState } from 'react'
import { Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
    useGetIdsHealthcareservicesHcsIdRequesters,
    useDeleteIdsHealthcareservicesHcsIdRequestersId,
} from '@toktokdoctech/tokapi-client'
import { debounce } from 'underscore'
import { ucfirst } from '@saas/lib/shared-utils'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import AddIcon from '@mui/icons-material/Add'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import useUserPermissions from '../../hooks/useUserPermissions'
import PageContent from '../Common/pageContent/PageContent'
import PageContentHeader from '../Common/pageContent/PageContentHeader'
import PageContentBody from '../Common/pageContent/PageContentBody'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import { useFormsStyles } from '../../styles'
import { useListControls } from '../../hooks/useListControls'
import sxs from './_sxs'
import TokTokDocDataTable from '../Common/DataTable'
import { deletionBanner, describeRequesterRow, getHeaders } from './utils/_utils'
import { RequestersTableData } from './_props'
import { useAppDispatch } from '../../store'
import { displaySnackbar } from '../../actions/main'
import DeletionBannerDefaultContent from '../Common/DeletionBanner/DeletionBannerDefaultContent'
import TokTokDocDialog from '../Common/Dialog'

const RequestersList = () => {
    const currentHcs = useCurrentHcs()
    const { hasUserPermission } = useUserPermissions()
    const theme = useTheme()
    const large = useMediaQuery(theme.breakpoints.up('sm'))
    const navigate = useNavigate()
    const formsClasses = useFormsStyles()
    const healthCareService = useCurrentHcs()
    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const [isBannerOpen, setIsBannerOpen] = useState(false)
    const [rowToDelete, setRowToDelete] = useState<RequestersTableData | null>(null)

    const {
        page,
        search,
        sort,
        itemsPerPage,
        handlePaginationChange,
        handleSearchChange,
        handleItemsPerPageChange,
        handleSort,
    } = useListControls(large ? 10 : 5)

    const limit = itemsPerPage
    const skip = page * itemsPerPage

    const { data, isLoading } = useGetIdsHealthcareservicesHcsIdRequesters(
        healthCareService?.id || '',
        { limit, search, skip },
        {
            query: {
                select: (freshData) => {
                    const computedRequesters = freshData.requesters.map((r) => ({
                        id: r.id,
                        rppsNum: r.rppsNum,
                        lastName: ucfirst(r.lastName),
                        firstName: ucfirst(r.firstName),
                        professionLabel: r.profession.label ? ucfirst(r.profession.label) : '',
                        specialityLabel: r.speciality?.label ? ucfirst(r.speciality.label) : '',
                        codeProfession: r.profession.code,
                        codeSpeciality: r.speciality?.code,
                        hasSecureEmail: !!r.securedEmail,
                        nationalId: r.nationalId,
                    }))
                    return { requesters: computedRequesters, total: freshData.total }
                },
                queryKey: ['requesters', healthCareService?.id, search, page, itemsPerPage],
            },
        },
    )

    const requesters = useMemo(() => {
        const baseRequesters = data?.requesters || []
        return sort === 'descending' ? [...baseRequesters].reverse() : baseRequesters
    }, [data, sort])

    const { mutate: deleteRequester, isPending } = useDeleteIdsHealthcareservicesHcsIdRequestersId({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['requesters', healthCareService?.id] })
                dispatch(displaySnackbar('success', 'Le requérant a été supprimé avec succès'))
                setIsBannerOpen(false)
            },
            onError: (err: unknown) => {
                const message = err instanceof Error ? err.message : String(err)
                dispatch(displaySnackbar('error', message))
            },
        },
    })

    const debouncedSearch = useMemo(() => debounce((value: string) => handleSearchChange(value), 300), [])

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            debouncedSearch.cancel()
        }
    }, [debouncedSearch])

    const renderEmptyState = () => {
        if (search.length === 0)
            return (
                <Box sx={sxs.requestersTableEmptyStateBox}>
                    <Typography sx={sxs.requestersTableText}>
                        Il n’y a pas encore de requérant dans l’établissement : commencez par{' '}
                        <Typography component={Link} to="/requester" sx={sxs.requestersTableActionButtonEdit}>
                            ajouter un requérant
                        </Typography>{' '}
                        pour pouvoir réaliser des téléexpertises.
                    </Typography>
                </Box>
            )
        return <Typography sx={sxs.requestersTableText}>Aucun résultat n’a été trouvé</Typography>
    }

    const handleDelete = () => {
        if (!rowToDelete) return

        deleteRequester({
            hcsId: healthCareService?.id || '',
            id: rowToDelete.id,
        })
    }

    return (
        <>
            <PageContent>
                <PageContentHeader
                    left={
                        <TextField
                            variant="outlined"
                            id="input-with-icon-adornment"
                            className={formsClasses.SearchField}
                            onChange={(e) => debouncedSearch(e.target.value)}
                            label={
                                <Typography className={formsClasses.SearchFieldLabel}>
                                    <SearchRoundedIcon />
                                    Recherche par nom et prénom
                                </Typography>
                            }
                        />
                    }
                    right={
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<AddIcon />}
                            component={Link}
                            to="/requester"
                            sx={sxs.requestersListButton}
                        >
                            <Typography component="span">Ajouter</Typography>
                        </Button>
                    }
                />
                <PageContentBody>
                    {requesters.length === 0 && !isLoading ? (
                        <Box sx={sxs.requestersTableEmptyStateBox}>{renderEmptyState()}</Box>
                    ) : (
                        <TokTokDocDataTable
                            loading={isLoading}
                            page={page}
                            itemsPerPage={itemsPerPage}
                            count={data?.total ?? 0}
                            handlePaginationChange={handlePaginationChange}
                            handleItemsPerPageChange={handleItemsPerPageChange}
                            headers={requesters.length > 0 ? getHeaders({ sort, onClick: handleSort, large }) : []}
                            items={requesters}
                            describeRows={(row) =>
                                describeRequesterRow({
                                    row,
                                    isAdmin: hasUserPermission('ADMIN'),
                                    large,
                                    onDelete: () => {
                                        setRowToDelete(row)
                                        setIsBannerOpen(true)
                                    },
                                    isRequesterValidationEnabled: currentHcs.isRequesterValidationEnabled,
                                })
                            }
                            onRowClick={(row) => navigate(`/requester/${row.id}`)}
                        />
                    )}
                </PageContentBody>
            </PageContent>

            <TokTokDocDialog
                isOpen={isBannerOpen}
                handleClose={() => setIsBannerOpen(false)}
                handleSave={handleDelete}
                loading={isPending}
                saveTitle="Supprimer"
                title={deletionBanner.title}
                subtitle={deletionBanner.subLabel}
                isDeleteAction
                disableEscape
            >
                <DeletionBannerDefaultContent />
            </TokTokDocDialog>
        </>
    )
}

export default RequestersList
